import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, CheckCircle, Clock, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { approveWalkIn, denyWalkIn, getWalkInRequest } from '@/services/api.client';
import { getApiErrorStatus } from '@/lib/api-error';
import { formatRelative } from '@/lib/utils';
import { toast } from 'sonner';

function CountdownTimer({ expiresAt }: { expiresAt: string }) {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    const update = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setRemaining('Expired');
        return;
      }
      const mins = Math.floor(diff / 60_000);
      const secs = Math.floor((diff % 60_000) / 1000);
      setRemaining(`${mins}:${secs.toString().padStart(2, '0')} remaining`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const isExpired = remaining === 'Expired';

  return (
    <span
      className={`flex items-center gap-1.5 text-sm font-medium ${
        isExpired ? 'text-destructive' : 'text-[var(--status-amber-text)]'
      }`}
    >
      <Clock className="size-4" />
      {remaining}
    </span>
  );
}

export function WalkInApprovalPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [denyReason, setDenyReason] = useState('');
  const [showDenyInput, setShowDenyInput] = useState(false);
  const [resolved, setResolved] = useState<'approved' | 'denied' | null>(null);

  const {
    data: request,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['walk-in', requestId],
    queryFn: () => getWalkInRequest(requestId ?? ''),
    enabled: !!requestId,
    refetchInterval: 15_000,
  });

  const approveMut = useMutation({
    mutationFn: () => approveWalkIn(requestId ?? ''),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      setResolved('approved');
    },
    onError: () => {
      toast.error('Action failed. Please try again.');
    },
  });

  const denyMut = useMutation({
    mutationFn: () => denyWalkIn(requestId ?? '', denyReason || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      setResolved('denied');
    },
    onError: () => {
      toast.error('Action failed. Please try again.');
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-md mx-auto space-y-4 pt-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !request) {
    const status = getApiErrorStatus(error);
    const message =
      status === 404
        ? 'This walk-in request has expired or was already resolved.'
        : status === 403
          ? 'You do not have access to this walk-in request.'
          : 'This walk-in request could not be loaded.';

    return (
      <div className="max-w-md mx-auto pt-8 text-center space-y-4">
        <AlertTriangle className="size-12 text-[var(--status-amber-text)] mx-auto" />
        <p className="text-sm text-muted-foreground">{message}</p>
        <Button variant="outline" onClick={() => navigate('/resident')}>
          Back to home
        </Button>
      </div>
    );
  }

  if (resolved === 'approved') {
    return (
      <div className="max-w-md mx-auto pt-12 text-center space-y-4">
        <CheckCircle className="size-16 text-[var(--status-green-text)] mx-auto" />
        <h2 className="text-xl">Entry approved</h2>
        <p className="text-sm text-muted-foreground">
          The guard has been notified. {request.visitorName} may enter.
        </p>
        <Button onClick={() => navigate('/resident')}>Back to home</Button>
      </div>
    );
  }

  if (resolved === 'denied') {
    return (
      <div className="max-w-md mx-auto pt-12 text-center space-y-4">
        <XCircle className="size-16 text-destructive mx-auto" />
        <h2 className="text-xl">Entry denied</h2>
        <p className="text-sm text-muted-foreground">The guard has been notified.</p>
        <Button onClick={() => navigate('/resident')}>Back to home</Button>
      </div>
    );
  }

  const isExpired =
    request.status === 'expired' || new Date(request.expiresAt).getTime() < Date.now();
  const isAlreadyResolved = request.status === 'approved' || request.status === 'denied';

  return (
    <div className="max-w-md mx-auto space-y-6 pt-4">
      <div className="text-center space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
          Walk-in request
        </p>
        <h1 className="text-2xl">Someone is at your gate</h1>
      </div>

      <Card className="border-[var(--border-accent)]">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{request.visitorName}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Phone</span>
            <span>{request.visitorPhone}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Unit</span>
            <span>{request.unitBeingVisited}</span>
          </div>
          {request.vehiclePlate && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Vehicle</span>
              <span className="font-mono">{request.vehiclePlate}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Guard</span>
            <span>{request.guardName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Requested</span>
            <span>{formatRelative(request.createdAt)}</span>
          </div>
          <div className="pt-1">
            <CountdownTimer expiresAt={request.expiresAt} />
          </div>
        </CardContent>
      </Card>

      {isExpired ? (
        <Card className="bg-[var(--status-amber-tint)] border-[var(--status-amber)]">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-sm text-[var(--status-amber-text)] font-medium">
              This request has expired. The guard has been notified.
            </p>
          </CardContent>
        </Card>
      ) : isAlreadyResolved ? (
        <Card className="bg-muted">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-sm text-muted-foreground capitalize">
              This request was already {request.status}.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {showDenyInput ? (
            <div className="space-y-3">
              <Textarea
                placeholder="Reason for denying (optional, shown to guard only)"
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                rows={3}
              />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDenyInput(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => denyMut.mutate()}
                  disabled={denyMut.isPending}
                >
                  {denyMut.isPending ? 'Denying…' : 'Confirm deny'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                className="flex-1 bg-[var(--status-green)] text-[var(--text-inverse)] hover:bg-[var(--status-green)]/90 h-12 text-base"
                onClick={() => approveMut.mutate()}
                disabled={approveMut.isPending || denyMut.isPending}
              >
                {approveMut.isPending ? (
                  'Approving…'
                ) : (
                  <>
                    <Check className="size-4" />
                    Allow entry
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-destructive text-destructive hover:bg-[var(--status-red-tint)] h-12 text-base"
                onClick={() => setShowDenyInput(true)}
                disabled={approveMut.isPending || denyMut.isPending}
              >
                <X className="size-4" />
                Deny
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
